import React, { createContext, useCallback, useContext, useMemo } from 'react';
import {
    buildPrintApiPayload,
    buildPrintTOCLookupMaps,
    getBranchNodeKeys,
    getSelectedAndTotalItemsCountByNodeKeys,
    PrintApiPayloadOptions,
} from '../components/common/PrintReportModal.utils';
import { PrintReportModalTOCItem } from '../data-types/printReportModalTOCItem';
import { IWhitePaperColumn, PassthruType, TaxReturnTOC, TOCSelectOption } from '../model';
import { PrintReportModalAction, PrintReportModalState } from '../reducers/printReportModalStateReducer';
import { buildTocItemsFromTOC } from '../utils/PrintReportModalStateUtils';
import { getFilteredTOCItems } from '../utils/TaxReturnTOCUtils';

export type { PrintReportModalAction };

export interface SelectedAndTotalItemsCount {
    selectedCount: number;
    totalCount: number;
}

interface IPrintReportModalContext {
    isLoadingFormTOC: boolean;
    searchText: string;
    filteredTocData: PrintReportModalTOCItem[];
    openNodeIds: Set<string>;
    checkedStateById: Map<string, boolean | 'mixed'>;
    selectedAndTotalItemsCount: SelectedAndTotalItemsCount;
    hasFormsSelectedForSubmit: boolean;
    submitPayload: TOCSelectOption[];
    onSelectableCheckedStateChange: (nodeId: string, checked: boolean | 'mixed') => void;
    onSelectableOpenNodeIdsChange: (nextOpenNodeIds: Set<string>) => void;
    onSearchChange: (searchText: string) => void;
    selectAllCheckboxState: boolean | 'mixed';
    onSelectAllChange: () => void;
    entities?: IWhitePaperColumn[] | null | undefined;
    selectedEntity?: IWhitePaperColumn;
    onEntitySelect?: (entity: IWhitePaperColumn | undefined) => void;
    isEntitySelectorEnabled: boolean;
    isDoNotPrintBlankFormsAvailable: boolean;
    onDoNotPrintBlankFormsCheckedStateChange: (checked: boolean) => void;
    doNotPrintBlankForms: boolean;
}

const PrintReportModalContext = createContext<IPrintReportModalContext | null>(null);

const filterVisiblePrintTocItems = (items: PrintReportModalTOCItem[]): PrintReportModalTOCItem[] => {
    const visibleItems: PrintReportModalTOCItem[] = [];

    items.forEach((item: PrintReportModalTOCItem): void => {
        const isVisibleInPrintView: boolean = item.isVisibleInPrintView ?? false;
        if (!isVisibleInPrintView) {
            return;
        }

        const childItems: PrintReportModalTOCItem[] = item.children ?? [];
        const visibleChildItems: PrintReportModalTOCItem[] = filterVisiblePrintTocItems(childItems);
        const hasChildren: boolean = childItems.length > 0;
        const hasVisibleChildren: boolean = visibleChildItems.length > 0;
        const hasOwnTocKey: boolean = item.taxReturnTOCKey !== null && item.taxReturnTOCKey !== undefined;

        if (hasChildren && !hasVisibleChildren && !hasOwnTocKey) {
            return;
        }

        const nextItem: PrintReportModalTOCItem = {
            ...item,
            children: hasVisibleChildren ? visibleChildItems : undefined,
        };

        visibleItems.push(nextItem);
    });

    return visibleItems;
};

export interface IPrintReportModalProviderProps {
    formTOCData?: TaxReturnTOC | null | undefined;
    isLoadingFormTOC?: boolean;
    children: React.ReactNode;
    entities: IWhitePaperColumn[];
    selectedEntity: IWhitePaperColumn;
    onEntitySelect: (entity: IWhitePaperColumn | undefined) => void;
    isEntitySelectorEnabled: boolean;
    shouldIncludeAllColumns: boolean;
    shouldSetAllColumns: boolean;
    isTotalEntitySelected: boolean;
    dispatch: React.Dispatch<PrintReportModalAction>;
    uiState: PrintReportModalState;
    isDoNotPrintBlankFormsAvailable: boolean;
    doNotPrintBlankForms: boolean;
}

export const PrintReportModalProvider: React.FC<IPrintReportModalProviderProps> = ({
    formTOCData,
    isLoadingFormTOC,
    children,
    entities,
    selectedEntity,
    onEntitySelect,
    isEntitySelectorEnabled,
    shouldIncludeAllColumns,
    shouldSetAllColumns,
    dispatch,
    uiState,
    isDoNotPrintBlankFormsAvailable,
    doNotPrintBlankForms,
}) => {
    const baseTocData: PrintReportModalTOCItem[] = useMemo((): PrintReportModalTOCItem[] => {
        return buildTocItemsFromTOC(formTOCData);
    }, [formTOCData]);

    const tocData: PrintReportModalTOCItem[] = baseTocData;
    const selectedNodeKeys: Set<string> = uiState.selectedNodeKeys;
    const openNodeIds: Set<string> = uiState.openNodeKeys;
    const searchText: string = uiState.searchText;

    const filteredTocData: PrintReportModalTOCItem[] = useMemo<PrintReportModalTOCItem[]>(() => {
        const searchFilteredItems: PrintReportModalTOCItem[] = getFilteredTOCItems(tocData, searchText) as PrintReportModalTOCItem[];
        return filterVisiblePrintTocItems(searchFilteredItems);
    }, [searchText, tocData]);

    const lookupMaps = useMemo((): ReturnType<typeof buildPrintTOCLookupMaps> => buildPrintTOCLookupMaps(tocData), [tocData]);
    const filteredLookupMaps = useMemo(
        (): ReturnType<typeof buildPrintTOCLookupMaps> => buildPrintTOCLookupMaps(filteredTocData),
        [filteredTocData]
    );
    const visibleLeafNodeKeys: Set<string> = lookupMaps.visibleLeafNodeKeys;
    const filteredParentNodeKeyByNodeKey: Map<string, string | null> = filteredLookupMaps.parentNodeKeyByNodeKey;
    const filteredChildNodeKeysByNodeKey: Map<string, string[]> = filteredLookupMaps.childNodeKeysByNodeKey;
    const visibleFilteredLeafNodeKeys: Set<string> = filteredLookupMaps.visibleLeafNodeKeys;

    const checkedStateById = useMemo<Map<string, boolean | 'mixed'>>(() => {
        const checkedStateMap: Map<string, boolean | 'mixed'> = new Map<string, boolean | 'mixed'>();

        const computeCheckedState = (nodeKey: string): boolean | 'mixed' => {
            const childNodeKeys: string[] = filteredChildNodeKeysByNodeKey.get(nodeKey) ?? [];
            const hasChildren: boolean = childNodeKeys.length > 0;

            if (!hasChildren) {
                const isChecked: boolean = selectedNodeKeys.has(nodeKey);
                checkedStateMap.set(nodeKey, isChecked);
                return isChecked;
            }

            let hasCheckedDescendant: boolean = false;
            let hasUncheckedDescendant: boolean = false;

            for (const childNodeKey of childNodeKeys) {
                const childCheckedState: boolean | 'mixed' = computeCheckedState(childNodeKey);

                if (childCheckedState === 'mixed') {
                    hasCheckedDescendant = true;
                    hasUncheckedDescendant = true;
                    continue;
                }

                if (childCheckedState) {
                    hasCheckedDescendant = true;
                    continue;
                }

                hasUncheckedDescendant = true;
            }

            let nodeCheckedState: boolean | 'mixed' = false;
            if (hasCheckedDescendant && hasUncheckedDescendant) {
                nodeCheckedState = 'mixed';
            } else if (hasCheckedDescendant) {
                nodeCheckedState = true;
            }

            checkedStateMap.set(nodeKey, nodeCheckedState);
            return nodeCheckedState;
        };

        for (const [nodeKey, parentNodeKey] of filteredParentNodeKeyByNodeKey.entries()) {
            const isRootNode: boolean = !parentNodeKey;
            if (isRootNode) {
                computeCheckedState(nodeKey);
            }
        }

        return checkedStateMap;
    }, [filteredChildNodeKeysByNodeKey, filteredParentNodeKeyByNodeKey, selectedNodeKeys]);

    const getLeafDescendantNodeKeys = useCallback(
        (nodeKey: string): string[] => {
            const leafNodeKeys: string[] = [];
            const stack: string[] = [nodeKey];

            while (stack.length > 0) {
                const currentNodeKey: string | undefined = stack.pop();
                if (!currentNodeKey) {
                    continue;
                }

                const childNodeKeys: string[] = filteredChildNodeKeysByNodeKey.get(currentNodeKey) ?? [];
                const hasChildren: boolean = childNodeKeys.length > 0;

                if (!hasChildren) {
                    leafNodeKeys.push(currentNodeKey);
                    continue;
                }

                for (const childNodeKey of childNodeKeys) {
                    stack.push(childNodeKey);
                }
            }

            return leafNodeKeys;
        },
        [filteredChildNodeKeysByNodeKey]
    );

    const onSelectableCheckedStateChange = useCallback(
        (nodeId: string, checked: boolean | 'mixed'): void => {
            const shouldSelect: boolean = checked === true || checked === 'mixed';
            const leafNodeKeys: string[] = getLeafDescendantNodeKeys(nodeId);
            dispatch({ type: 'checkbox-check-state-changed', nodeId, checked, leafNodeKeys, shouldSelect });
        },
        [dispatch, getLeafDescendantNodeKeys]
    );

    const onSelectableOpenNodeIdsChange = useCallback(
        (nextOpenNodeIds: Set<string>): void => {
            const nextOpenNodeIdsCopy: Set<string> = new Set<string>(nextOpenNodeIds);
            dispatch({ type: 'node-expansion-state-changed', nextOpenNodeIds: nextOpenNodeIdsCopy });
        },
        [dispatch]
    );

    const onSearchChange = useCallback(
        (search: string): void => {
            const normalizedSearchText: string = search.toLowerCase().trim();
            let nextOpenNodeKeys: Set<string> = new Set<string>();

            if (normalizedSearchText) {
                const filteredItems: PrintReportModalTOCItem[] = getFilteredTOCItems(
                    tocData,
                    normalizedSearchText
                ) as PrintReportModalTOCItem[];
                const visibleFilteredItems: PrintReportModalTOCItem[] = filterVisiblePrintTocItems(filteredItems);
                nextOpenNodeKeys = getBranchNodeKeys(visibleFilteredItems);
            }

            dispatch({ type: 'search-state-change', searchText: normalizedSearchText, openNodeKeys: nextOpenNodeKeys });
        },
        [dispatch, tocData]
    );

    const selectedAndTotalItemsCount: SelectedAndTotalItemsCount = useMemo<SelectedAndTotalItemsCount>(() => {
        return getSelectedAndTotalItemsCountByNodeKeys(selectedNodeKeys, visibleLeafNodeKeys);
    }, [selectedNodeKeys, visibleLeafNodeKeys]);

    const hasFormsSelectedForSubmit: boolean = useMemo<boolean>(() => {
        return selectedAndTotalItemsCount.selectedCount > 0;
    }, [selectedAndTotalItemsCount.selectedCount]);

    const printApiPayloadOptions: PrintApiPayloadOptions = useMemo<PrintApiPayloadOptions>(() => {
        const isPartnershipPartnerColumn: boolean =
            formTOCData?.returnType === PassthruType.Partnership && selectedEntity?.columnId === 0 && shouldIncludeAllColumns;

        let allColumn: PrintApiPayloadOptions['allColumn'] = 'omit';
        if (shouldIncludeAllColumns) {
            allColumn = shouldSetAllColumns ? 'mirrorPrint' : 'false';
        }

        return {
            selectionMode: isPartnershipPartnerColumn ? 'matchSelectedFormPageId' : 'visibleAndSelected',
            allColumn,
        };
    }, [formTOCData?.returnType, selectedEntity?.columnId, shouldIncludeAllColumns, shouldSetAllColumns]);

    const submitPayload: TOCSelectOption[] = useMemo<TOCSelectOption[]>(() => {
        return buildPrintApiPayload(formTOCData, selectedNodeKeys, printApiPayloadOptions);
    }, [formTOCData, selectedNodeKeys, printApiPayloadOptions]);

    const visibleFilteredItemsCount: SelectedAndTotalItemsCount = useMemo<SelectedAndTotalItemsCount>(() => {
        const selectedCount: number = Array.from(visibleFilteredLeafNodeKeys).filter((nodeKey: string) =>
            selectedNodeKeys.has(nodeKey)
        ).length;
        const totalCount: number = visibleFilteredLeafNodeKeys.size;
        return { selectedCount, totalCount };
    }, [visibleFilteredLeafNodeKeys, selectedNodeKeys]);

    const selectAllCheckboxState: boolean | 'mixed' = useMemo(() => {
        const { selectedCount, totalCount } = visibleFilteredItemsCount;
        if (selectedCount === 0) {
            return false;
        }
        if (selectedCount === totalCount) {
            return true;
        }
        return 'mixed';
    }, [visibleFilteredItemsCount]);

    const onSelectAllChange = useCallback((): void => {
        const shouldSelectAll = selectAllCheckboxState !== true;

        visibleFilteredLeafNodeKeys.forEach((nodeKey: string) => {
            onSelectableCheckedStateChange(nodeKey, shouldSelectAll);
        });
    }, [onSelectableCheckedStateChange, selectAllCheckboxState, visibleFilteredLeafNodeKeys]);

    const onDoNotPrintBlankFormsCheckedStateChange = useCallback(
        (checked: boolean): void => {
            dispatch({ type: 'do-not-print-blank-forms-check-state-changed', checked: checked });
        },
        [dispatch]
    );

    return (
        <PrintReportModalContext.Provider
            value={{
                isLoadingFormTOC: !!isLoadingFormTOC,
                searchText,
                filteredTocData,
                openNodeIds,
                checkedStateById,
                selectedAndTotalItemsCount,
                hasFormsSelectedForSubmit,
                submitPayload,
                onSelectableCheckedStateChange,
                onSelectableOpenNodeIdsChange,
                onSearchChange,
                selectAllCheckboxState,
                onSelectAllChange,
                entities,
                onEntitySelect,
                selectedEntity,
                isEntitySelectorEnabled,
                isDoNotPrintBlankFormsAvailable,
                onDoNotPrintBlankFormsCheckedStateChange,
                doNotPrintBlankForms,
            }}
        >
            {children}
        </PrintReportModalContext.Provider>
    );
};

export function usePrintReportModal() {
    const ctx = useContext(PrintReportModalContext);
    if (!ctx) {
        throw new Error('usePrintReportModal must be used within a PrintReportModalProvider');
    }
    return ctx;
}

export default PrintReportModalProvider;
